import { useCallback, useMemo, useState } from 'react';

import type {
  UseDateTimeBlockDatePaginationInterface,
  UseDateTimeBlockDatePaginationReturnInterface,
} from '../types/use-date-pagination.types';
import { useDateTimeBlockDisabledItems } from './use-disabled-items';

/**
 * Custom hook to handle pagination logic for DateTimeBlock.Dates component.
 *
 * Manages pagination state, calculates visible dates for current page,
 * handles navigation between pages, and provides utilities for checking
 * disabled dates.
 *
 * @param params - Configuration object containing dates, display options, and callbacks
 * @returns Object containing pagination state, computed values, and event handlers
 */
export function useDateTimeBlockDatePagination({
  dates,
  datesToShow,
  disabledDates,
  disablePagination = false,
}: UseDateTimeBlockDatePaginationInterface): UseDateTimeBlockDatePaginationReturnInterface {
  /* Pagination state */
  const [currentPage, setCurrentPage] = useState(0);

  /* Calculate pagination values */
  const totalPages = useMemo(() => {
    if (disablePagination) return 1;
    return Math.ceil(dates.length / datesToShow);
  }, [dates.length, datesToShow, disablePagination]);

  /* Get visible dates for current page */
  const visibleDates = useMemo(() => {
    if (disablePagination) {
      return dates;
    }

    if (currentPage === totalPages - 1) {
      /*
       * On the last page, show the last datesToShow items
       * to ensure the last set is always "full"
       */
      const startIndex = Math.max(0, dates.length - datesToShow);
      return dates.slice(startIndex);
    }
    /* Normal pagination logic for all other pages */
    const startIndex = currentPage * datesToShow;
    const endIndex = startIndex + datesToShow;
    return dates.slice(startIndex, endIndex);
  }, [dates, currentPage, datesToShow, totalPages, disablePagination]);

  /* Use disabled items check hook for efficient disabled date checking */
  const isDateDisabled = useDateTimeBlockDisabledItems(disabledDates);

  /* Calculate if prev/next buttons should be disabled */
  const isPrevDisabled = disablePagination || currentPage === 0;
  const isNextDisabled = disablePagination || currentPage >= totalPages - 1;

  /* Navigation handlers */
  const handlePrevious = useCallback(() => {
    if (!isPrevDisabled) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
    }
  }, [isPrevDisabled, currentPage]);

  const handleNext = useCallback(() => {
    if (!isNextDisabled) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
    }
  }, [isNextDisabled, currentPage]);

  return useMemo(
    () => ({
      currentPage,
      totalPages,
      visibleDates,
      isPrevDisabled,
      isNextDisabled,
      isDateDisabled,
      handlePrevious,
      handleNext,
    }),
    [
      currentPage,
      totalPages,
      visibleDates,
      isPrevDisabled,
      isNextDisabled,
      isDateDisabled,
      handlePrevious,
      handleNext,
    ],
  );
}
